import { useState, useEffect, useCallback } from 'react';
import { billingService, Subscription, Invoice, BillingOverview, SubscriptionFilters } from '../services';

interface UseBillingOverviewReturn {
  overview: BillingOverview | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

export const useBillingOverview = (): UseBillingOverviewReturn => {
  const [overview, setOverview] = useState<BillingOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await billingService.getOverview();
      setOverview(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch billing overview'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  return {
    overview,
    loading,
    error,
    refetch: fetchOverview,
  };
};

interface UseSubscriptionsReturn {
  subscriptions: Subscription[];
  loading: boolean;
  error: Error | null;
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: SubscriptionFilters;
  setFilters: (filters: SubscriptionFilters) => void;
  refetch: () => void;
  cancelSubscription: (id: string, reason?: string) => Promise<void>;
  updateSubscription: (id: string, data: Partial<Subscription>) => Promise<Subscription>;
}

export const useSubscriptions = (initialFilters: SubscriptionFilters = {}): UseSubscriptionsReturn => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [filters, setFilters] = useState<SubscriptionFilters>({
    page: 1,
    limit: 20,
    ...initialFilters,
  });
  const [meta, setMeta] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await billingService.getSubscriptions(filters);
      setSubscriptions(response.data);
      setMeta(response.meta);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch subscriptions'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const cancelSubscription = async (id: string): Promise<void> => {
    await billingService.cancelSubscription(id);
    setSubscriptions((prev) =>
      prev.map((s) => (s.id === id ? { ...s, status: 'canceled' } : s))
    );
  };

  const updateSubscription = async (id: string, data: Partial<Subscription>): Promise<Subscription> => {
    const updated = await billingService.updateSubscription(id, data);
    setSubscriptions((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...updated } : s))
    );
    return updated;
  };

  return {
    subscriptions,
    loading,
    error,
    meta,
    filters,
    setFilters,
    refetch: fetchSubscriptions,
    cancelSubscription,
    updateSubscription,
  };
};

interface UseInvoicesReturn {
  invoices: Invoice[];
  loading: boolean;
  error: Error | null;
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: { tenantId?: string; status?: string; search?: string; startDate?: string; endDate?: string; page?: number; limit?: number };
  setFilters: (filters: { tenantId?: string; status?: string; search?: string; startDate?: string; endDate?: string; page?: number; limit?: number }) => void;
  refetch: () => void;
  refundInvoice: (id: string, amount?: number) => Promise<void>;
}

export const useInvoices = (initialFilters: { tenantId?: string; status?: string; search?: string; startDate?: string; endDate?: string; page?: number; limit?: number } = {}): UseInvoicesReturn => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [filters, setFilters] = useState(initialFilters);
  const [meta, setMeta] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await billingService.getInvoices(filters);
      setInvoices(response.data);
      setMeta(response.meta);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch invoices'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const refundInvoice = async (id: string, amount?: number): Promise<void> => {
    await billingService.refundInvoice(id, amount);
    setInvoices((prev) =>
      prev.map((inv) => (inv.id === id ? { ...inv, status: 'refunded' } : inv))
    );
  };

  return {
    invoices,
    loading,
    error,
    meta,
    filters,
    setFilters,
    refetch: fetchInvoices,
    refundInvoice,
  };
};

interface UseInvoiceDetailReturn {
  invoice: Invoice | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  refundInvoice: (amount?: number) => Promise<void>;
}

export const useInvoiceDetail = (id?: string): UseInvoiceDetailReturn => {
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchInvoice = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await billingService.getInvoiceById(id);
      setInvoice(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch invoice detail'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);

  const refundInvoice = async (amount?: number): Promise<void> => {
    if (!id) return;
    await billingService.refundInvoice(id, amount);
    setInvoice((prev) => (prev ? { ...prev, status: 'refunded' } : prev));
  };

  return {
    invoice,
    loading,
    error,
    refetch: fetchInvoice,
    refundInvoice,
  };
};

interface UseRevenueReportReturn {
  report: Array<{
    period: string;
    revenue: number;
    newSubscriptions: number;
    churnedSubscriptions: number;
  }>;
  loading: boolean;
  error: Error | null;
  fetchReport: (params: { startDate: string; endDate: string; groupBy: 'day' | 'week' | 'month' }) => void;
}

export const useRevenueReport = (): UseRevenueReportReturn => {
  const [report, setReport] = useState<Array<{
    period: string;
    revenue: number;
    newSubscriptions: number;
    churnedSubscriptions: number;
  }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchReport = useCallback(async (params: { startDate: string; endDate: string; groupBy: 'day' | 'week' | 'month' }) => {
    setLoading(true);
    setError(null);
    try {
      const response = await billingService.getRevenueReport(params);
      setReport(response.data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch revenue report'));
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    report,
    loading,
    error,
    fetchReport,
  };
};
